import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notification, Button, Flex } from 'antd'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { useVoidChecksContext } from '../index.context'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { TRANSACTION_STATUS_LABEL } from '../../search-result/index.constant'
import { voidACheck } from 'src/app/shared/services/ViewTransactionService'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import DeleteIssuedCheckModal from '../../search-result/components/DeleteIssuedCheckModal'

const VoidChecksResultTable: React.FC = () => {
  const { t } = useTranslation()
  const { 
    transactionData, 
    searchLoading, 
    outerTableRef, 
    handleSearch, 
    reloadTable,
    handleDownload, 
    currentPage, 
    pageSize 
  } = useVoidChecksContext()
  const { userPermissions } = useRolePermission()
  const [api, contextHolder] = notification.useNotification()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteCheckNumber, setDeleteCheckNumber] = useState('')
  const [deleteId, setDeleteId] = useState('')

  const canVoid = useMemo(
    () => userPermissions[ESubModules.ALL_TRANS]?.includes(EPermissions.AVOID_ISSUED),
    [userPermissions]
  )

  const canDelete = useMemo(
    () => userPermissions[ESubModules.ADD_NEW_ISSUE_CHK]?.includes(EPermissions.DELETE_ITEMS),
    [userPermissions]
  )

  const handleVoid = useCallback(async (id: string, checkNumber: string) => {
    try {
      const response = await voidACheck(id)
      
      if (response?.statusCode === 'Success') {
        api.success({
          message: (
            <RichTextField
              richText={t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CHECK_NUMBER_VOID_SUCCESSFULLY, {
                checkNumber,
              })}
            />
          ),
          closeIcon: null,
        })
        // Reload table preserving all current state (page, sorting, columns, etc.)
        reloadTable()
      }
    } catch (error) {
      console.error('Error voiding check:', error)
    }
  }, [api, t, reloadTable])

  const columnDefs: TableColumnDefs[] = useMemo(
    () => [
      {
        field: 'rowNumber',
        headerName: t('VoidChecks:table.no'),
        colId: 'rowNumber',
        defaultSelect: true,
        sortable: false,
        width: 80,
        valueGetter: (params: any) => {
          if (params.node && params.node.rowIndex !== null && params.node.rowIndex !== undefined) {
            return params.node.rowIndex + 1
          }
          return ''
        },
      },
      {
        field: 'clientName',
        headerName: t('VoidChecks:table.clientName'),
        colId: 'clientName',
        sortable: false,
        defaultSelect: true,
      },
      {
        field: 'checkNumber',
        headerName: t('VoidChecks:table.checkNumber'),
        colId: 'checkNumber',
        defaultSelect: true,
      },
      {
        field: 'amount',
        headerName: t('VoidChecks:table.amount'),
        colId: 'amount',
        defaultSelect: true,
        cellRenderer: (props: any): string => {
          return props.value ? `$${props.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''
        },
      },
      {
        field: 'issuedDate',
        headerName: t('VoidChecks:table.issuedDate'),
        colId: 'issuedDate',
        defaultSelect: true,
        dataType: 'date',
        valueType: 'date',
      },
      {
        field: 'void',
        headerName: t('VoidChecks:table.action'),
        colId: 'void',
        sortable: false,
        defaultSelect: true,
        cellRenderer: (props: any): JSX.Element => {
          const isVoidDisabled = !(
            props.data?.status !== TRANSACTION_STATUS_LABEL.VOID &&
            props.data?.status !== TRANSACTION_STATUS_LABEL.EXCEPTION &&
            props.data?.status !== TRANSACTION_STATUS_LABEL.RETURNED &&
            props.data?.status !== TRANSACTION_STATUS_LABEL.PAID &&
            canVoid &&
            props.data?.transactionType === 'Check'
          )
          const isDeleteDisabled = !(
            props.data?.status === TRANSACTION_STATUS_LABEL.VOID &&
            canDelete &&
            props.data?.transactionType === 'Check'
          )
          return (
            <Flex gap={8} style={{ width: '100%' }}>
              <Button
                type='primary'
                disabled={isVoidDisabled}
                style={{
                  flex: 1,
                  opacity: isVoidDisabled ? 0.4 : 1,
                  cursor: isVoidDisabled ? 'not-allowed' : 'pointer',
                }}
                onClick={() => handleVoid(props.data?.id || '', props.data?.checkNumber || '')}
              >
                {t('VoidChecks:actions.void')}
              </Button>
              <Button
                danger
                disabled={isDeleteDisabled}
                style={{
                  flex: 1,
                  opacity: isDeleteDisabled ? 0.4 : 1,
                  cursor: isDeleteDisabled ? 'not-allowed' : 'pointer',
                }}
                onClick={() => {
                  setDeleteCheckNumber(props.data?.checkNumber || '')
                  setDeleteId(props.data?.id || '')
                  setIsDeleteModalOpen(true)
                }}
              >
                {t('common:delete')}
              </Button>
            </Flex>
          )
        },
      },
    ],
    [t, canVoid, canDelete, handleVoid]
  )

  return (
    <>
      {contextHolder}
      <NewCommonTable
        ref={outerTableRef}
        tableName={TABLE_NAME.VOID_CHECKS_RESULTS}
        rowData={transactionData.items}
        total={transactionData.total}
        handleSearch={handleSearch}
        handleDownload={handleDownload}
        loading={searchLoading}
        isGettingList={searchLoading}
        columnDefs={columnDefs}
        displayActionColumn={false}
        currentPage={currentPage}
        pageSize={pageSize}
        displaySearch={false}
        displayColumnBtn={false}
        displayDownloadBtn={false}
        displayResetButton={false}
        displayPagination={true}
        onColumnResized={() => {}}
      />

      <DeleteIssuedCheckModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        checkNumber={deleteCheckNumber}
        id={deleteId}
        notiApi={api}
        onGridReady={() => reloadTable()}
      />
    </>
  )
}

export default VoidChecksResultTable
